import React, {
  FC,
  useState,
  useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DEFAULT_THEME_COLOR } from 'appenv';
import styled from 'styled-components';
import { Transition } from 'react-transition-group';
import { Avatar } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import CloseIcon from '@material-ui/icons/Close';
import UserAvatar from 'components/UserAvatar';
import ShowMore from 'components/utils/ShowMore';
import {
  selectEnrichmentDataShow,
  selectattendeeEnrichmentData,
  setEnrichmentDataShow,
  setEnrichmentData,
  EnrichmentData,
} from 'models/enrichmentData';
import UserDetails from 'components/exhibitorPanel/UserDetails';
import AttendeePersonalInfo from './AttendeePersonalInfo';
import AttendeeCompanyInfo from './AttendeeCompanyInfo';
import AttendeeActivityLog from './AttendeeActivityLog';
import AttendeeActivityLogObserver from './AttendeeActivityLogObserver';

const AttendeeEnrichmentDataContainer = styled.div<{ state: string }>`
  width: 314px;
  height: calc(100% - 64px);
  position: fixed;
  top: 64px;
  right: ${({ state }) => (state === 'entered' ? 0 : -400)}px;
  background: white;
  overflow-y: auto;
  box-shadow: -1px 1px 4px 1px #D4D4D4;
  transition: right 0.4s;
  z-index: 1000;
`;

const StyledCloseIcon = styled(CloseIcon)`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
`;

const AttendeeInfoContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 16px;
  border-bottom: 1px solid #DFE2E6;
  text-align: center;
`;

const StyledTabs = styled(Tabs)<{ $color: string }>`
  & .tab-indicator {
    background-color: ${({ $color }) => $color};
  }
`;

const StyledTab = styled(Tab)<{ $color: string }>`
  text-transform: none;
  min-width: 50px;
  padding: 6px;
  font-family: Lato;
  color: ${({ $color }) => $color} !important;
`;

const AttendeeTagsContainer = styled.div`
  width: 100%;
  text-align: left;
`;

const UserAvatarContainer = styled.div`
  width: 100%;
  height: 64px;
  margin: 16px 0 8px 0;
  display: flex;
  justify-content: center;
`;

const StyledAvatar = styled(Avatar)`
  width: 64px;
  height: 64px;
`;

const AttendeeName = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: -0.40799999237060547px;
  color: #484848;
  margin: 8px;
`;

const AttendeeBio = styled.div`
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  color: #A2AAB3;
  margin: 8px;
`;

const AttendeePersonalInfoContainer = styled.div<{ shorten?: number }>`
  width: 100%;
  padding: 16px;
  overflow-y: auto;
  color: #484848;
`;

const AttendeeEnrichmentData:FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState(0);
  const showEnrichmentData = useSelector<any, boolean>(selectEnrichmentDataShow);
  const enrichment = useSelector(selectattendeeEnrichmentData);
  const { themeColor = DEFAULT_THEME_COLOR } = useSelector((s) => s.eventConfigs);

  const handleChange = (_event, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleCloseEnrichmentComponent = useCallback(() => {
    dispatch(setEnrichmentDataShow(false));
    dispatch(setEnrichmentData(null));
  }, [dispatch]);

  if (!enrichment) return null;
  const { person, company, attendee } = enrichment;

  return (
    <Transition in={showEnrichmentData} timeout={0} mountOnEnter>
      {
        ((state) => (
          <AttendeeEnrichmentDataContainer state={state}>
            <StyledCloseIcon
              onClick={handleCloseEnrichmentComponent}
            />
            <AttendeeInfoContainer>
              {person?.avatar ? (
                <UserAvatarContainer>
                  <StyledAvatar src={person?.avatar} />
                </UserAvatarContainer>
              ) : (
                <UserAvatarContainer>
                  <UserAvatar
                    name={person?.name?.givenName}
                    style={{
                      margin: '16px',
                      transform: 'scale(2)',
                    }}
                  />
                </UserAvatarContainer>
              )}
              <AttendeeName>
                {attendee?.name || person?.name?.fullName}
              </AttendeeName>
              {(person?.bio || attendee?.desc) && (
                <ShowMore maxHeight={100}>
                  <AttendeeBio>
                    {person?.bio || attendee?.desc}
                  </AttendeeBio>
                </ShowMore>
              )}
              <AttendeeTagsContainer>
                <UserDetails
                  seniority={person?.employment?.seniority}
                  employees={company?.metrics?.employees}
                  estimatedAnnualRevenue={company?.metrics?.estimatedAnnualRevenue}
                />
              </AttendeeTagsContainer>
            </AttendeeInfoContainer>
            <StyledTabs
              $color={themeColor}
              value={tabIndex}
              classes={{ indicator: 'tab-indicator' }}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              scrollButtons="auto"
              aria-label="scrollable prevent tabs"
            >
              <StyledTab $color={themeColor} label={t('attendee_enrichment.personal_info', 'Personal Info')} />
              <StyledTab $color={themeColor} label={t('attendee_enrichment.company', 'Company')} />
              <StyledTab $color={themeColor} label={t('attendee_enrichment.activity_log', 'Activity Log')} />
            </StyledTabs>
            <AttendeePersonalInfoContainer>
              {tabIndex === 0 && (
                <AttendeePersonalInfo
                  fullName={person?.name?.fullName}
                  employment={person?.employment}
                  location={person?.location}
                  site={person?.site}
                  github={person?.github?.handle}
                  twitter={person?.twitter?.handle}
                  facebook={person?.facebook?.handle}
                  linkedin={person?.linkedin?.handle}
                  attendee={attendee}
                />
              )}
              {tabIndex === 1 && (
                <AttendeeCompanyInfo
                  companyDesciption={company?.description}
                  name={company?.name}
                  employees={company?.metrics?.employees}
                  estimatedAnnualRevenue={company?.metrics?.estimatedAnnualRevenue}
                  industry={company?.category?.industry}
                  sector={company?.category?.sector}
                  foundedYear={company?.foundedYear}
                  type={company?.type}
                  location={company?.location}
                  timeZone={company?.timeZone}
                  sicCode={company?.category?.sicCode}
                  naicsCode={company?.category?.naicsCode}
                  linkedin={company?.linkedin?.handle}
                />
              )}
              {tabIndex === 2 && (
                <>
                  <AttendeeActivityLogObserver userUuid={attendee.uuid} />
                  <AttendeeActivityLog />
                </>
              )}
            </AttendeePersonalInfoContainer>
          </AttendeeEnrichmentDataContainer>
        ))
      }
    </Transition>
  );
};

export default AttendeeEnrichmentData;
